import { useSelector } from 'react-redux';
import { ClientAccessSettingsProps } from '../../../../ClientAccess.types';
import {
  beFiPlanSubFeatures,
  currentFinancesSubFeatures,
  financialPlanSubFeatures,
} from './Dashboard.meta';
import {
  currentClientSelector,
  dashboardAccessOptionsSelector,
  dashboardAccessWorkflowPresetsSelector,
} from '../../../../../AdviserArea/AdviserAreaSelector';
import { DashboardFeatureId } from '../../../../../AdviserArea/AdviserArea.types';

export const useDashboard = ({
  dashboardAccess,
  defaultDashboardAccessSet,
  onboardingWorkflow,
  onChange,
}: ClientAccessSettingsProps) => {
  const currentClient = useSelector(currentClientSelector);
  const dashboardAccessWorkflowPresets = useSelector(dashboardAccessWorkflowPresetsSelector);
  const dashboardAccessOptions = useSelector(dashboardAccessOptionsSelector);

  const hasDashboardAccess = dashboardAccess ? dashboardAccess.length > 0 : false;

  const workflowPresets =
    onboardingWorkflow !== null && dashboardAccessWorkflowPresets[onboardingWorkflow]
      ? dashboardAccessWorkflowPresets[onboardingWorkflow]
      : [];

  const disabledDashboardAccessOptions = workflowPresets
    .filter(({ IsReadOnly }) => IsReadOnly)
    .map(({ DashboardAccess }) => DashboardAccess);

  const toggleDashboardAccess = (isChecked: boolean) => {
    if (isChecked) {
      onChange({
        dashboardAccess:
          workflowPresets.length > 0
            ? workflowPresets.map(({ DashboardAccess: id }) => id)
            : defaultDashboardAccessSet,
      });
    } else {
      onChange({
        dashboardAccess: null,
      });
    }
  };

  const toggleSubFeature = (isChecked: boolean, featureId: DashboardFeatureId) => {
    if (dashboardAccess) {
      onChange({
        dashboardAccess: isChecked
          ? [...dashboardAccess, featureId]
          : dashboardAccess.filter(id => id !== featureId),
      });
    }
  };

  const toggleFeature = (isChecked: boolean, featureId: DashboardFeatureId) => {
    if (dashboardAccess) {
      onChange({
        dashboardAccess: isChecked
          ? [
              ...(dashboardAccess?.filter(id => {
                switch (featureId) {
                  // Remove BeFi tools if FP is enabled
                  case DashboardFeatureId.FinancialPlan:
                    return !beFiPlanSubFeatures.includes(id);
                  default:
                    return true;
                }
              }) ?? []),
              featureId,
            ]
          : dashboardAccess.filter(id => {
              switch (featureId) {
                case DashboardFeatureId.FinancialPlan:
                  return id !== featureId && !financialPlanSubFeatures.includes(id);
                case DashboardFeatureId.CurrentFinances:
                  return id !== featureId && !currentFinancesSubFeatures.includes(id);
                default:
                  return id !== featureId;
              }
            }),
      });
    }
  };

  return {
    accessSet: dashboardAccess || [],
    isOrionClient: !!currentClient?.IsOrionClient,
    dashboardAccessOptions,
    hasDashboardAccess,
    disabledDashboardAccessOptions,
    toggleDashboardAccess,
    toggleSubFeature,
    toggleFeature,
  };
};
